import { Paragraph, paragraphDTO } from './data-model.paragraph';
import { Sentences } from './data-model.sentences';
import { editStateEnumType } from './data-model.enums';
import { CapicheLoggerCore } from '@capiche/ts-logger-core';

const logger: CapicheLoggerCore = new CapicheLoggerCore('paragraphs:', false);

export class Paragraphs {
  constructor(public readonly paragraphs: Paragraph[]) {
    logger.log('constructor:', paragraphs);
  }

  // Factory: from DTOs
  static fromDTOs(dtos: paragraphDTO[]): Paragraphs {
    return new Paragraphs(dtos.map((dto) => Paragraph.fromDTO(dto)));
  }

  // Factory: from raw multi-paragraph text
  static fromString(text: string, seqId?: number, srcId?: string): Paragraphs {
    logger.log('fromString:', text, seqId, srcId);
    const splitParagraphs = splitIntoParagraphs(text); // Utility returns { text: string; index: number }[]

    const paragraphs = splitParagraphs.map(({ text: paragraphText, index }) =>
      Paragraph.fromString(paragraphText, index, seqId, srcId),
    );

    return new Paragraphs(paragraphs);
  }

  // Serialization
  toDTOs(): paragraphDTO[] {
    return this.paragraphs.map((p) => p.toDTO());
  }

  // Collection utilities

  filter(predicate: (p: Paragraph) => boolean): Paragraphs {
    return new Paragraphs(this.paragraphs.filter(predicate));
  }

  map(fn: (p: Paragraph) => Paragraph): Paragraphs {
    return new Paragraphs(this.paragraphs.map(fn));
  }

  // Business logic
  filterOnEditState(retainStates: editStateEnumType[]): Paragraphs {
    return this.filter((p) => retainStates.includes(p.dto.editState)).map(
      (p) =>
        new Paragraph({
          ...p.dto,
          sentences: Sentences.fromDTOs(p.dto.sentences).filterOnEditState(retainStates).toDTOs(),
        }),
    );
  }

  // Aggregation utilities
  getTotalSentenceCount(): number {
    return this.paragraphs.reduce((total, p) => total + p.getSentenceCount(), 0);
  }

  getTotalTokenCount(): number {
    return this.paragraphs.reduce((total, p) => total + p.getTotalTokenCount(), 0);
  }

  getCorrectedParagraphs(): Paragraphs {
    return this.filter((p) => p.isCorrected());
  }

  hasMisspelledTokens(): boolean {
    return this.paragraphs.some((p) => p.hasMisspelledTokens());
  }
}

/**
 * Splits input text into { text, index } pairs for paragraphs,
 * using newline patterns while retaining accurate offsets.
 *
 * - Splits on one or more consecutive newline characters (`\n+`).
 * - Trims leading whitespace for each paragraph while adjusting the index.
 * - Filters out empty or whitespace-only segments.
 *
 * @param inputString The full multiline text to split into paragraphs.
 * @returns An array of { text: string, index: number } for each paragraph.
 */
export function splitIntoParagraphs(inputString: string): { text: string; index: number }[] {
  // CSW-1400 manual-test finding (2026-07-05): the previous implementation
  // used `inputString.split(/\n+/)`, which discards the actual delimiter
  // text — String.split() never tells you how many characters each match
  // consumed — then assumed every delimiter was exactly 1 char
  // (`currentOffset += raw.length + 1`). A single `\n` is the exception, not
  // the rule: a real paragraph break (pressing Enter twice) is `\n\n`, 2
  // chars, so every such boundary undercounted by 1 — silently shifting
  // every subsequent paragraph's `index` (and everything derived from it:
  // sentence/token offsets, highlight positions) left by 1 character, and
  // by 2 after the next paragraph break, compounding by one full character
  // per paragraph. Reported as "contenteditable highlights slip by one char
  // per paragraph" and is almost certainly also the deeper cause of the
  // textarea overlay drift previously attributed to scrollbar-width/wrap-
  // point issues alone — that only ever showed up once there was enough
  // content for 2+ paragraphs to exist, i.e. exactly when this compounds
  // into something visible.
  //
  // Fixed by using a global regex with exec() in a loop instead of split():
  // RegExpExecArray.index and match[0].length give the delimiter's real
  // position and length directly, so each paragraph's start is computed
  // from actual string positions rather than an assumed fixed-width
  // separator.
  const paragraphSplitter = /\n+/g;

  const paragraphs: { text: string; index: number }[] = [];
  const pieces: { raw: string; start: number }[] = [];

  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = paragraphSplitter.exec(inputString)) !== null) {
    pieces.push({ raw: inputString.slice(lastIndex, match.index), start: lastIndex });
    lastIndex = match.index + match[0].length;
  }
  pieces.push({ raw: inputString.slice(lastIndex), start: lastIndex });

  for (const { raw, start } of pieces) {
    if (raw.trim().length > 0) {
      // Trim leading whitespace while adjusting index
      const trimmed = raw.replace(/^\s*/, '');
      const leadingWhitespaceLength = raw.length - trimmed.length;

      paragraphs.push({
        text: trimmed,
        index: start + leadingWhitespaceLength,
      });
    }
  }

  return paragraphs;
}
